import React from 'react';
import * as _ from 'lodash';
import {updateQueryStringParam, getCloudHomeUrl, getNetAppConsoleUrl} from '../../common/utils';
import {PAGE_NOT_FOUND_QUERY_KEY} from '../../common/constants';

export const ThankYouMessage = ({email}) => {
    return (
        <p id="thank-you-msg">Information will be emailed to {email} shortly about participating in our trial program.</p>
    );
};
ThankYouMessage.displayName = 'ThankYouMessage';

export const TenantCreationFailedMessage = ({email}) => {
    return (
        <React.Fragment>
            <p id="tenant-creation-failed-msg" className='tw-mt-6px'>
                At this time, we are unable to process your tenant request automatically. We are working hard to
                rectify this issue as soon as possible.
                In the meantime, our team has received your request and will process it within 24 hours.
            </p>
            <p className='tw-mt-6px'>
                When your tenant is ready, we will send an email to {email} with details on getting started using Data
                Infrastructure Insights.
            </p>
            <p className='tw-mt-6px'>
                If you do not hear back from us in 24 hours, you can reach out to us at
                <a id="cust-sup-feedback-email" className='tw-text-blue-300' href="mailto:ng-cloudinsights-customerfeedback@netapp.com"> ng-cloudinsights-customerfeedback@netapp.com. </a>
            </p>
        </React.Fragment>
    );
};
TenantCreationFailedMessage.displayName = 'TenantCreationFailedMessage';

export const InvalidRegistrationFormMessage = () => {
    return (
        <a id="sign-up-url" className='tw-text-blue-300' href="/signup">Click here to sign up again.</a>
    );
};
InvalidRegistrationFormMessage.displayName = 'InvalidRegistrationFormMessage';

export const PageNotFoundMessage = ({notFoundUrl}) => {
    const pageUrl = updateQueryStringParam(window.location.href, PAGE_NOT_FOUND_QUERY_KEY, undefined);
    window.history.replaceState({cighs: 'notfound'}, null, pageUrl);

    return (
        <React.Fragment>
            <p id="page-not-found-msg" className='tw-mt-6px tw-pb-8px'>
                We&rsquo;re sorry, we couldn&rsquo;t find the page you requested <strong>{notFoundUrl}</strong>
            </p>
            <p className='tw-pt-20px tw-pb-12px'>
                <a id="go-home" className='tw-text-blue-300' href={window.location.origin + '/service'}>Go back to the home page</a>
            </p>
        </React.Fragment>
    );
}
PageNotFoundMessage.displayName = 'PageNotFoundMessage';


export const NetAppEmployeeRestrictedMessage = ({email, serviceUrl}) => {
    const serviceUrlLink = serviceUrl + '/netapp-internal/cloud-insights';
    return (
        <React.Fragment>
            <p id="netapp-emp-restrict-msg" className='tw-mt-6px'>
                As a NetApp employee, you do not need an individual Data Infrastructure Insights tenant but can instead be onboarded to an existing tenant for collaboration
                with other fellow NetApp employees. This Data Infrastructure Insights tenant is configured with various types of data collectors and dashboards that are suitable
                for product learning and ideal for customer demos. The tenant also often serves as a preview of new product features,
                which you can explore and provide feedback on before they become generally available.
            </p>
            <p className='tw-mt-6px'>
                Send an email to <a id="internal-onboard-email" className='tw-text-blue-300' href="mailto:ng-cloudinsights-internalonboarding@netapp.com">ng-cloudinsights-internalonboarding@netapp.com </a>
                and request to be invited to the demo tenant to start showcasing Data Infrastructure Insights value to customers.
            </p>
            <p className='tw-mt-6px'>
                Please include the following email address in your request.<br></br>
                <strong>{email}</strong>
            </p>
            <p className='tw-mt-6px'>
                To learn more about Data Infrastructure Insights, or to access supporting sales tools, please visit the
                <a id="ct-service-url" className='tw-text-blue-300' href={serviceUrlLink}> Data Infrastructure Insights NetApp Internal page.</a>
            </p>
        </React.Fragment>
    );
};
NetAppEmployeeRestrictedMessage.displayName = 'NetAppEmployeeRestrictedMessage';


export const InvalidEmailMessage = () => {
    return (
        <React.Fragment>
            <p id="invalid-email-msg" className='tw-mt-6px'>
                The email address you are using appears to be a personal or invalid address.
                The Data Infrastructure Insights free trial tenant requires a valid business email address,
                which is your email address at the company where you work.
            </p>
            <p id="error-receive-email" className='tw-mt-6px'>
                Please sign in (or sign up) on NetApp Console using your business email address,
                and click the Data Infrastructure Insights “Start Free Trial” button to start your free trial.
            </p>
            <p id="receive-msg-error" className='tw-mt-6px'>
                If you believe you are receiving this message in error, you can reach out to
                <a id="cust-sup-feedback-email" className='tw-text-blue-300' href="mailto:ng-cloudinsights-customerfeedback@netapp.com"> ng-cloudinsights-customerfeedback@netapp.com </a>
                for assistance with setting up a Data Infrastructure Insights trial tenant.
            </p>
        </React.Fragment>
    );
};
InvalidEmailMessage.displayName = 'InvalidEmailMessage';


export const EmailValidatedFailedMessage = ({email}) => {
    return (
        <React.Fragment>
            <p id="email-validation-failed-msg" className='tw-mt-6px'>
                The email address {email} for your account must be verified before starting your free trial of Data Infrastructure Insights.
                Click the link in the NetApp Console account sign-up email to verify your email address. If you do not have this email,
                you can resend the verification link from your account user settings on <a id="ct-api-url" className='tw-text-blue-300' href={getCloudHomeUrl()} rel="noreferrer noopener" target="_blank"> NetApp Console</a>.
                Once your email is verified, return to <a id="gw-url" className='tw-text-blue-300' href={window.location.origin + '/service'}>Data Infrastructure Insights</a> to start your free trial.
            </p>
            <p className='tw-mt-6px'>
                If you believe you are receiving this message in error, you can reach out to
                <a id="cust-sup-feedback-email" className='tw-text-blue-300' href="mailto:ng-cloudinsights-customerfeedback@netapp.com"> ng-cloudinsights-customerfeedback@netapp.com </a>
                for assistance with setting up a Data Infrastructure Insights trial tenant.
            </p>
        </React.Fragment>
    );
};
EmailValidatedFailedMessage.displayName = 'EmailValidatedFailedMessage';


export const SsoNotAllowNonNetAppEmployeeMessage = ({email}) => {
    return (
        <React.Fragment>
            <p id="sso-not-allow-netapp-msg" className='tw-mt-6px'>
                You have logged in to NetApp Console using NetApp Single Sign-On authentication, but the email address you are using&nbsp;
                <strong>{email}</strong> appears to be a non-NetApp email address.
            </p>
            <p className='tw-mt-6px'>
                The Data Infrastructure Insights free trial tenant requires a valid business email address, which is your email address at the company where you work.
                You cannot use NetApp Single Sign-On when logging in with your business email address.
            </p>
            <p className='tw-mt-6px'>
                Please sign in (or sign up) on <a id="ct-api-url" className='tw-text-blue-300' href={getCloudHomeUrl()} rel="noreferrer noopener" target="_blank"> NetApp Console </a>
                using your business email address and click the Data Infrastructure Insights &quot;Start Free Trial&quot; to start your trial.
            </p>
        </React.Fragment>
    );
};
SsoNotAllowNonNetAppEmployeeMessage.displayName = 'SsoNotAllowNonNetAppEmployeeMessage';


export const LoginRedirectionErrorMessage = ({message, ciSite}) => {
    return (
        <React.Fragment>
            <p id="login-redirect-error-msg" className='tw-mt-6px'>
                {message}
            </p>
            {
                !_.isNil(ciSite) && <p id="redirect-error-site" className='tw-pt-12px'> Data Infrastructure Insights: {ciSite}</p>
            }
        </React.Fragment>
    );
};
LoginRedirectionErrorMessage.displayName = 'LoginRedirectionErrorMessage';


export const AuthenticationFailureErrorMessage = ({message}) => {
    return (
        <React.Fragment>
            <p id="authentication-redirect-error-msg" className='tw-mt-6px'>
                {message}
            </p>
            <p id="login-error-msg" className='tw-mt-6px tw-mb-6px'>
                You may have pressed the back button, refreshed during login, opened too many login dialogs,
                or there is some issue with cookies. Try logging in again.
            </p>
        </React.Fragment>
    );
};
AuthenticationFailureErrorMessage.displayName = 'AuthenticationFailureErrorMessage';


export const Auth0ServerValidationFailureErrorMessage = ({message}) => {
    return (
        <React.Fragment>
            <p id="auth0-validation-redirect-error-msg" className='tw-mt-6px'>
                {message}
            </p>
            <p id="login-auth0-validation-error-msg" className='tw-mt-6px tw-mb-6px'>
                We are having trouble logging you in. Please try again after sometime.
            </p>
        </React.Fragment>
    );
};
Auth0ServerValidationFailureErrorMessage.displayName = 'Auth0ServerValidationFailureErrorMessage';


export const DisabledStandaloneUiModeErrorMessage = () => {
    return (
        <React.Fragment>
            <p id="access-endpoint-error-msg" className='tw-mt-6px tw-mb-6px'>
                Access to this endpoint is not available in the current environment. Please proceed using the
                <a id="ct-api-url" className='tw-text-blue-300' href={getNetAppConsoleUrl() + '/dii'} rel="noreferrer noopener" target="_self"> NetApp Console </a>
                link.
            </p>
        </React.Fragment>
    );
};
DisabledStandaloneUiModeErrorMessage.displayName = 'DisabledStandaloneUiModeErrorMessage';
